import { useMemo } from "react";
import {
  estimatedRenovationCost, totalProjectCost, estimatedSoftCosts,
  estimatedInteriorCost, estimatedExteriorCost, estimatedSystemsCost,
  estimatedExtensionsCost,
  formatCurrency, formatPct,
} from "@/lib/calculations";
import { useFormatCurrency } from "@/lib/CurrencyContext";
import {
  actualsByCategory, actualsTotal, CATEGORY_LABEL,
} from "@/lib/actuals";

/**
 * Actual-mode Overview. Reuses the dashboard surface to tell a plan-vs-actual
 * story without a separate "Spend Log" tab.
 */
export default function ActualOverview({ project }) {
  const fmt = useFormatCurrency();
  const actuals = project.actuals || [];
  const spent = actualsTotal(actuals);
  const byCat = useMemo(() => actualsByCategory(actuals), [actuals]);

  // Plan figures — reuse existing estimation helpers
  const planInterior   = estimatedInteriorCost(project);
  const planExtensions = estimatedExtensionsCost(project);
  const planExterior   = estimatedExteriorCost(project);
  const planSystems    = estimatedSystemsCost(project);
  const planReno       = estimatedRenovationCost(project);
  const planSoft       = estimatedSoftCosts(project);
  const planPurchase   = Number(project.purchase_price) || 0;
  const planTotal      = totalProjectCost(project);

  const rows = [
    { key: "purchase",   label: "Purchase",   plan: planPurchase },
    { key: "interior",   label: "Interior",   plan: planInterior },
    { key: "extensions", label: "Extensions", plan: planExtensions },
    { key: "exterior",   label: "Exterior",   plan: planExterior },
    { key: "systems",    label: "Systems",    plan: planSystems },
    { key: "soft",       label: "Soft Costs", plan: planSoft },
    { key: "other",      label: "Other",      plan: 0 },
  ];

  const remaining = planTotal - spent;
  const sale = Number(project.target_sale_price) || 0;
  // Projected final profit = sale − (purchase + actuals on reno + soft remaining at plan)
  // Simpler + defensible: sale − max(planTotal, spent).
  const projectedProfit = sale - Math.max(planTotal, spent);
  const pctOfBudget = planTotal > 0 ? (spent / planTotal) * 100 : 0;

  return (
    <div className="space-y-6 fc-fade-in" data-testid="actual-overview">
      {/* Top cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card
          label="Actual Spent"
          value={fmt(spent)}
          sub={`${actuals.length} entr${actuals.length === 1 ? "y" : "ies"} logged`}
          tid="actual-metric-spent"
        />
        <Card
          label="Remaining Budget"
          value={fmt(remaining)}
          sub={planTotal > 0 ? `${pctOfBudget.toFixed(0)}% of plan used` : "Set a plan to track"}
          tone={remaining < 0 ? "text-[#DC2626]" : "text-[#1C1C1C]"}
          tid="actual-metric-remaining"
        />
        <Card
          label="Projected Final Profit"
          value={fmt(projectedProfit)}
          sub={sale > 0 ? `At target sale ${fmt(sale)}` : "Set a target sale price"}
          tone={projectedProfit >= 0 ? "text-[#059669]" : "text-[#DC2626]"}
          tid="actual-metric-projected-profit"
        />
        <Card
          label="% of Budget Used"
          value={formatPct(pctOfBudget / 100)}
          sub={pctOfBudget > 100 ? "Over plan" : "On track"}
          tone={pctOfBudget > 100 ? "text-[#DC2626]" : pctOfBudget > 85 ? "text-slate-brand-500" : "text-slate-brand-700"}
          tid="actual-metric-pct"
        />
      </div>

      {/* Category breakdown */}
      <div className="fc-card p-6">
        <div className="flex items-end justify-between mb-4">
          <div>
            <h2 className="fc-heading text-lg text-[#1C1C1C]">Plan vs Actual by category</h2>
            <p className="text-xs text-[#78716C] mt-1">
              Where your spend is tracking. Switch to the Rooms, Exterior, Systems or Soft Costs tabs to log entries.
            </p>
          </div>
          <div className="text-right">
            <div className="fc-label">Total planned</div>
            <div className="fc-heading text-base text-[#1C1C1C] fc-mono">{fmt(planTotal)}</div>
          </div>
        </div>
        <div className="space-y-3">
          {rows.map((r) => {
            const actual = byCat[r.key] || 0;
            const pct = r.plan > 0 ? (actual / r.plan) * 100 : actual > 0 ? 100 : 0;
            const over = r.plan > 0 && actual > r.plan;
            const bar = over
              ? "bg-[#DC2626]"
              : pct > 85
              ? "bg-slate-brand-500"
              : "bg-slate-brand-300";
            return (
              <div key={r.key} data-testid={`plan-vs-actual-${r.key}`}>
                <div className="flex items-center justify-between text-xs text-[#57534E]">
                  <div className="font-medium text-[#1C1C1C]">{r.label}</div>
                  <div className="fc-mono">
                    <span className={over ? "text-[#DC2626]" : "text-[#1C1C1C]"}>{fmt(actual)}</span>
                    <span className="text-[#A8A29E]"> / {fmt(r.plan)}</span>
                  </div>
                </div>
                <div className="mt-1.5 h-1.5 w-full bg-black/[0.05] rounded-full overflow-hidden">
                  <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {actuals.length === 0 && (
        <div className="fc-card fc-slate p-8 text-center" data-testid="actuals-overview-empty">
          <div className="fc-heading text-[#1C1C1C]">You haven't logged any spend yet</div>
          <p className="text-sm text-slate-brand-700 mt-1.5">
            Jump to Rooms, Exterior, Systems or Soft Costs to record receipts against the plan.
          </p>
        </div>
      )}
    </div>
  );
}

function Card({ label, value, sub, tid, tone = "text-[#1C1C1C]" }) {
  return (
    <div className="fc-card p-5" data-testid={tid}>
      <div className="fc-label">{label}</div>
      <div className={`fc-heading text-3xl tracking-tight mt-2 ${tone}`}>{value}</div>
      {sub && <div className="text-xs text-[#A8A29E] mt-2">{sub}</div>}
    </div>
  );
}
